import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ActiveEvents, pageDetails } from '../Recoil/ActiveEvents';
import moment from 'moment';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, MapPinIcon } from '@heroicons/react/24/outline';

function Banner({ onSlideChange }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeEvents, setActiveEvents] = useRecoilState(ActiveEvents);
  const [currIndex, setCurrIndex] = useRecoilState(pageDetails);
  const [isLoading, setIsLoading] = useState(true);
  const [autoSlide, setAutoSlide] = useState(true);

  useEffect(() => {
    // Load active events for the banner
    let eventURI = process.env.REACT_APP_API_URL + "/Events/GetUpcomingEvents";
    fetch(eventURI, { method: "GET" })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        let events = !!data.EventsType && !data.EventsType[0].ErrorMessage ? data.EventsType : []
        setActiveEvents(events);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching events:', error);
        setIsLoading(false);
      });
  }, [setActiveEvents]);

  // Auto-slide functionality
  useEffect(() => {
    if (autoSlide && activeEvents.length > 1) {
      const interval = setInterval(() => {
        const newIndex = (currentIndex + 1) % activeEvents.length;
        setCurrentIndex(newIndex);
        if (onSlideChange) {
          onSlideChange(newIndex);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [autoSlide, activeEvents.length, currentIndex, onSlideChange]);

  const nextSlide = () => {
    const newIndex = (currentIndex + 1) % activeEvents.length;
    setCurrentIndex(newIndex);
    if (onSlideChange) {
      onSlideChange(newIndex);
    }
    setTimeout(() => {
      setCurrIndex({ ...currIndex, currentIndex: currentIndex })
    }, 100);
  };

  const prevSlide = () => {
    const newIndex = (currentIndex - 1 + activeEvents.length) % activeEvents.length;
    setCurrentIndex(newIndex);
    if (onSlideChange) {
      onSlideChange(newIndex);
    }
    setTimeout(() => {
      setCurrIndex({ ...currIndex, currentIndex: currentIndex })
    }, 100);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    if (onSlideChange) {
      onSlideChange(index);
    }
    setAutoSlide(false);
    setTimeout(() => setAutoSlide(true), 10000); // Resume auto-slide after 10 seconds
  };

  if (isLoading) {
    return (
      <div className="relative w-full h-[60vh] md:h-[80vh] lg:h-[90vh] mt-20 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 flex items-center justify-center">
        <div className="text-center space-y-6 animate-fade-in">
          <div className="w-16 h-16 mx-auto mb-6">
            <div className="w-full h-full border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Welcome to <span className="text-gradient-primary">Kalingarayan Runners</span>
          </h2>
          <p className="text-xl md:text-2xl text-neutral-300 max-w-2xl mx-auto leading-relaxed">
            Discover amazing running events and join our vibrant community
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <div className="shimmer w-32 h-8 rounded-lg"></div>
            <div className="shimmer w-24 h-8 rounded-lg"></div>
            <div className="shimmer w-28 h-8 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!activeEvents || activeEvents.length === 0) {
    return (
      <div className="relative w-full h-[60vh] md:h-[80vh] lg:h-[90vh] mt-20 bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 flex items-center justify-center">
        <div className="text-center space-y-6 animate-slide-up px-4">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Welcome to <span className="text-yellow-300">Kalingarayan Runners</span>
          </h2>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Join our passionate community of runners and discover exciting events that challenge and inspire you
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <button className="btn-secondary">
              Explore Events
            </button>
            <button className="btn-outline border-white text-white hover:bg-white hover:text-primary-500">
              Join Community
            </button>
          </div>
        </div>
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/10 rounded-full blur-xl animate-float animation-delay-300"></div>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full h-[70vh] md:h-[85vh] lg:h-[90vh] mt-20 overflow-hidden"
      onMouseEnter={() => setAutoSlide(false)}
      onMouseLeave={() => setAutoSlide(true)}
    >
      {/* Background Slides */}
      <div 
        className="flex transition-transform duration-700 ease-out relative w-full h-full" 
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {activeEvents.map((item, index) => (
          <div className="flex-shrink-0 w-full h-full relative" key={index}>
            <div className="absolute inset-0  z-10"></div>
            <img 
              src={item.ImgUrl} 
              alt={item.EventName} 
             /* className="h-full w-full object-contain object-center bg-neutral-900" */
             className="h-full w-full object-contain "
              loading={index === 0 ? "eager" : "lazy"}
            />
            

          </div>
        ))}
      </div>
      
      {/* Navigation Controls */}
      {activeEvents.length > 1 && (
        <>
          <button
            className="absolute top-1/2 left-4 md:left-8 transform -translate-y-1/2 glass-dark p-3 md:p-4 rounded-full hover:bg-white/20 transition-all duration-300 shadow-strong z-30 group"
            onClick={prevSlide}
            aria-label="Previous event"
          >
            <ChevronLeftIcon className="w-6 h-6 md:w-8 md:h-8 text-white group-hover:scale-110 transition-transform" />
          </button>
          
          <button
            className="absolute top-1/2 right-4 md:right-8 transform -translate-y-1/2 glass-dark p-3 md:p-4 rounded-full hover:bg-white/20 transition-all duration-300 shadow-strong z-30 group"
            onClick={nextSlide}
            aria-label="Next event"
          >
            <ChevronRightIcon className="w-6 h-6 md:w-8 md:h-8 text-white group-hover:scale-110 transition-transform" />
          </button>
        </>
      )}

      {/* Slide Indicators */}
      {activeEvents.length > 1 && (
        <div className="absolute bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-30">
          {activeEvents.map((_, index) => (
            <button
              key={index}
              className={`w-3 h-3 md:w-4 md:h-4 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-white scale-125 shadow-glow-primary' 
                  : 'bg-white/50 hover:bg-white/75 hover:scale-110'
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to event ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Progress Bar */}
      {autoSlide && activeEvents.length > 1 && (
        <div className="absolute bottom-0 left-0 w-full h-1 bg-white/20 z-30">
          <div 
            className="h-full bg-primary-500 transition-all duration-[5000ms] ease-linear"
            style={{ width: autoSlide ? '100%' : '0%' }}
          ></div>
        </div>
      )}
      
      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-10 w-32 h-32 bg-primary-500/10 rounded-full blur-2xl animate-float"></div>
      <div className="absolute bottom-1/4 left-10 w-24 h-24 bg-secondary-500/10 rounded-full blur-2xl animate-float animation-delay-300"></div>
    </div>
  );
}

export default Banner;